import { useEffect, useRef, useState, Fragment } from 'react'
import {
  useForm,
  useFormContext,
  FormProvider,
  Controller,
} from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Text, Group, Stack, Collapse, Loader } from '@mantine/core'
import {
  Autocomplete,
  LoadScriptProps,
  useJsApiLoader,
} from '@react-google-maps/api'
import {
  Phone,
  MapPin,
  User,
  PhoneIncoming,
  PhoneOutgoing,
  ChevronUp,
  ChevronDown,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { format, isValid, parseISO } from 'date-fns'

// Import our custom UI components
import { TextInput } from '@/components/ui/TextInput'
import { Select } from '@/components/ui/Select'
import { CallDirectionToggle } from '@/components/ui/ToggleGroup'
import { DateInput } from '@/components/ui/DateInput'
import { TimeInput } from '@/components/ui/TimeInput'

// Import validation schema and types
import {
  callerInformationSchema,
  defaultCallerInformationValues,
  CallerInformationFormValues,
} from '@/validation/schemas/callerInformation'
import { CallerInformationFormProps } from '@/types/cases'
import {
  callDirectionOptions,
  genderOptions,
  prefixOptions,
  callTypeOptions as defaultCallTypeOptions,
  getSubcategoriesForCallType,
} from './caseConstants'
import { useDispatcher } from '@/providers/DispatcherProvider'
import { useDebounce } from '@/hooks/useDebounce'
import { useCallerHistory } from '@/hooks/api'
import type { NormalizedCallerHistoryEntry } from '@/types/api/softphone'

const formatCallerHistoryDateTime = (value?: string) => {
  if (!value) return '—'

  const parsed = isValid(new Date(value)) ? new Date(value) : parseISO(value)

  if (!isValid(parsed)) return value

  return format(parsed, 'MMM d, yyyy | hh:mm a').replace(/\s(AM|PM)$/i, match =>
    match.toLowerCase()
  )
}

const CallerHistoryTable = ({
  entries,
  isLoading,
  isError,
  isPhoneReady,
}: {
  entries: NormalizedCallerHistoryEntry[]
  isLoading: boolean
  isError: boolean
  isPhoneReady: boolean
}) => {
  const [expanded, setExpanded] = useState(true)
  const hasEntries = entries.length > 0

  return (
    <div className="space-y-2 bg-off-white p-4 rounded-md">
      <Text size="sm" fw={600} c="gray.7">
        Caller History
      </Text>

      {!isPhoneReady ? (
        <div className="p-3 rounded-md">
          <Text size="sm" c="gray.6" fs="italic">
            Enter a complete phone number to load caller history
          </Text>
        </div>
      ) : isLoading ? (
        <div className="flex items-center gap-2 p-3 rounded-md">
          <Loader size="sm" color="primary" />
          <Text size="sm" c="gray.6">
            Loading caller history...
          </Text>
        </div>
      ) : isError ? (
        <div className="p-3 rounded-md">
          <Text size="sm" c="red.6" fs="italic">
            Unable to load caller history
          </Text>
        </div>
      ) : !hasEntries ? (
        <div className="p-3 rounded-md">
          <Text size="sm" c="gray.6" fs="italic">
            No previous cases found for this caller
          </Text>
        </div>
      ) : (
        <div className="rounded-md border border-gray-200 overflow-hidden bg-white">
          <Collapse in={expanded}>
            <div className="overflow-x-auto">
              <table className="w-full table-fixed border-collapse">
                <thead>
                  <tr className="bg-gray-100 border-b border-gray-200">
                    <th className="w-[28%] px-3 py-2 text-left text-xs font-medium text-gray-600 border-r border-gray-200">
                      Date & Time
                    </th>
                    {/* <th className="w-[24%] px-3 py-2 text-left text-xs font-medium text-gray-600 border-r border-gray-200">
                      Case Type
                    </th> */}
                    <th className="w-[24%] px-3 py-2 text-left text-xs font-medium text-gray-600 border-r border-gray-200">
                      Dispatcher
                    </th>
                    <th className="w-[24%] px-3 py-2 text-left text-xs font-medium text-gray-600">
                      Outcome
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map(entry => (
                    <Fragment key={entry.id}>
                      <tr className="border-b border-gray-200">
                        <td className="px-3 py-2 text-xs text-gray-800 border-r border-gray-200 align-top">
                          {formatCallerHistoryDateTime(entry.dateTime)}
                        </td>
                        {/* <td className="px-3 py-2 text-xs text-gray-800 border-r border-gray-200 align-top">
                          {entry.caseType || '—'}
                        </td> */}
                        <td className="px-3 py-2 text-xs text-gray-800 border-r border-gray-200 align-top">
                          {entry.dispatcherName || '—'}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-800 align-top">
                          {entry.outcome || '—'}
                        </td>
                      </tr>
                      {entry.note ? (
                        <tr className="border-b border-gray-200 bg-gray-50">
                          <td colSpan={4} className="px-3 py-2">
                            <Text size="xs" c="gray.6" fs="italic">
                              &quot;{entry.note}&quot;
                            </Text>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </Collapse>

          <button
            type="button"
            onClick={() => setExpanded(prev => !prev)}
            className="w-full flex items-center justify-between gap-3 px-3 py-2.5 bg-white border-t border-gray-200 cursor-pointer hover:bg-gray-50"
          >
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-primary text-white text-[10px] font-semibold">
                {entries.length}
              </span>
              <Text size="xs" c="gray.7">
                Previous cases found for this caller
              </Text>
            </div>
            {expanded ? (
              <ChevronUp className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            )}
          </button>
        </div>
      )}
    </div>
  )
}

// Default call category options (fallback)
// const defaultCallCategoryOptions: SelectOption[] = [
//   { value: 'medical_emergency', label: 'Medical Emergency' },
//   { value: 'traffic_accident', label: 'Traffic Accident' },
//   { value: 'fire_emergency', label: 'Fire Emergency' },
//   { value: 'police_emergency', label: 'Police Emergency' },
//   { value: 'general_inquiry', label: 'General Inquiry' },
//   { value: 'appointment_booking', label: 'Appointment Booking' },
//   { value: 'follow_up', label: 'Follow Up' },
//   { value: 'other', label: 'Other' },
// ]

/**
 * CallerInformation Component
 *
 * A comprehensive form component for capturing caller information in case creation.
 * Supports both standalone mode (manages its own form state) and formContext mode
 * (integrates with parent form via React Hook Form context).
 *
 * @param mode - 'standalone' for independent form, 'formContext' for parent form integration
 * @param initialValues - Default values for form fields
 * @param onSubmit - Callback when form is submitted (standalone mode)
 * @param onChange - Callback when form values change (live updates)
 * @param callTypeOptions - Custom call type options (optional)
 * @param callCategoryOptions - Custom call category options (optional)
 * @param className - Additional CSS classes
 * @param disabled - Disable all form inputs
 */

const libraries: LoadScriptProps['libraries'] = ['places']
const googleMapApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY

export default function CallerInformation({
  mode = 'standalone',
  initialValues = {},
  onSubmit,
  onChange,
  callTypeOptions = defaultCallTypeOptions,
  className = '',
  disabled = false,
  callDuration,
}: CallerInformationFormProps) {
  const [autocomplete, setAutoComplete] =
    useState<google.maps.places.Autocomplete | null>(null)
  const { dispatcher } = useDispatcher()

  // Create form instance for standalone mode
  const standaloneForm = useForm<CallerInformationFormValues>({
    resolver: zodResolver(callerInformationSchema),
    defaultValues: {
      ...defaultCallerInformationValues,
      ...initialValues,
    },
    mode: 'onChange',
  })

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: googleMapApiKey,
    libraries: libraries,
  })

  // Get form context for formContext mode
  const formContext = useFormContext<CallerInformationFormValues>()

  // Use appropriate form instance based on mode
  const form = mode === 'standalone' ? standaloneForm : formContext

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = form

  // Watch form values for live updates
  const watchedValues = watch()

  // Watch the specific call_type field
  const currentCallType = watch('call_type')

  // Watch phone number and debounce for caller history lookup
  const currentPhoneNumber = watch('phone_number')
  const debouncedPhoneNumber = useDebounce(currentPhoneNumber || '', 500)
  const normalizedDebouncedPhone = debouncedPhoneNumber.replace(/[^\d+]/g, '')
  const isPhoneReady = normalizedDebouncedPhone.replace(/\D/g, '').length >= 10

  const {
    data: callerHistoryData,
    isLoading: isCallerHistoryLoading,
    isError: isCallerHistoryError,
  } = useCallerHistory(debouncedPhoneNumber, dispatcher?.dispatcher_id)

  useEffect(() => {
    if (callDuration) {
      form.setValue('call_duration', callDuration, {
        shouldDirty: false,
        shouldValidate: false,
      })
    }
  }, [callDuration, form])

  // Get subcategories based on selected call type
  const currentSubcategories = getSubcategoriesForCallType(
    currentCallType || ''
  )

  // Handle form submission
  const handleFormSubmit = (values: CallerInformationFormValues) => {
    if (onSubmit) {
      onSubmit(values)
    }
  }

  const onLoad = (autocompleteInstance: google.maps.places.Autocomplete) => {
    setAutoComplete(autocompleteInstance)
  }

  const onPlaceChanged = () => {
    if (autocomplete) {
      try {
        const place = autocomplete.getPlace()

        if (!place.geometry || !place.formatted_address) {
          throw new Error('Invalid place data received.')
        }

        // Extract location data
        const lat = place.geometry?.location?.lat()
        const lng = place.geometry?.location?.lng()
        const formattedAddress = place.formatted_address

        // Extract state from address components
        let state = ''
        const addressComponents = place.address_components || []
        for (const component of addressComponents) {
          if (component.types.includes('administrative_area_level_1')) {
            state = component.long_name
            break
          }
        }

        // Update form with location data
        form.setValue('location', formattedAddress)
        if (lat && lng) {
          form.setValue('location_latitude', lat)
          form.setValue('location_longitude', lng)
        }
        if (state) {
          form.setValue('location_state', state)
        }
      } catch (error: any) {
        if (error?.message?.includes('CORS')) {
          toast.error(
            'There was an issue resolving the address. Please try again.'
          )
        } else {
          toast.error('Please select location again.')
        }
      }
    } else {
      toast.error('Autocomplete is not loaded yet.')
    }
  }

  // Handle live form changes
  // Use a ref to track previous values and avoid calling onChange on every render
  const prevValuesRef = useRef<string>('')

  useEffect(() => {
    if (onChange && watchedValues) {
      // Exclude call_duration from comparison since it's updated programmatically every second
      // and not by user input - we only want to trigger onChange for actual user changes
      const { ...valuesWithoutDuration } = watchedValues
      const currentValuesStr = JSON.stringify(valuesWithoutDuration)
      // Only call onChange if values have actually changed
      if (currentValuesStr !== prevValuesRef.current) {
        prevValuesRef.current = currentValuesStr
        onChange(watchedValues) // Still pass full values including call_duration
      }
    }
  }, [watchedValues, onChange])

  // Clear call category when call type changes
  useEffect(() => {
    const currentCategory = watch('call_category')
    if (currentCallType && currentCategory) {
      const categoryExists = currentSubcategories.find(
        cat => cat.value === currentCategory
      )

      // If current category is not available for the new call type, clear it
      if (!categoryExists) {
        form.setValue('call_category', undefined as any)
      }
    }
  }, [currentCallType, currentSubcategories, form, watch])

  // Render form content
  const renderFormContent = () => (
    <div
      className={`space-y-6 border border-light-gray-20 p-4 gap-4 rounded-md w-full min-w-2xl min-h-0 overflow-y-auto ${className}`}
    >
      {/* Call Information Section */}
      <div className="">
        <div className="flex items-center gap-2 mb-md border-b border-light-gray-20 pb-1 mb-4">
          <User color="#64676A" width={20} height={20} />
          <Text size="md" fw={600} c="grey.4">
            Call Information
          </Text>
        </div>

        <Controller
          name="call_direction"
          control={control}
          defaultValue="incoming"
          render={({ field }) => (
            <CallDirectionToggle
              label=""
              options={callDirectionOptions.map(option => ({
                ...option,
                icon:
                  option.value === 'incoming' ? (
                    <PhoneIncoming className="w-4 h-4" />
                  ) : (
                    <PhoneOutgoing className="w-4 h-4" />
                  ),
              }))}
              value={field.value}
              onChange={field.onChange}
              error={errors.call_direction?.message}
              disabled={disabled}
              className="w-full"
            />
          )}
        />
      </div>

      {/* Caller Information Section */}
      <div className="">
        <Group mb="md">
          <Text size="md" fw={600} c="primary.6">
            Caller Information
          </Text>
        </Group>

        <Stack gap="md">
          {/* Phone Number and Call Duration Row */}
          <Group grow>
            <TextInput
              label="Phone Number"
              placeholder="+23490875533"
              required
              register={register('phone_number')}
              error={errors.phone_number?.message}
              leftSection={<Phone className="w-4 h-4 text-gray-400" />}
              disabled={disabled}
            />

            <Controller
              name="call_duration"
              control={control}
              render={({ field }) => (
                <TimeInput
                  label="Call Duration"
                  required
                  value={field.value || '00:00:00'}
                  onChange={value => field.onChange(value)}
                  error={errors.call_duration?.message}
                  disabled={disabled}
                  readOnly
                />
              )}
            />

            {/* Gender Row */}
            <Controller
              name="gender"
              control={control}
              render={({ field }) => (
                <Select
                  label="Gender"
                  placeholder="Select gender"
                  required
                  data={genderOptions}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.gender?.message}
                  disabled={disabled}
                />
              )}
            />
          </Group>

          {/* Caller History Section */}
          <CallerHistoryTable
            entries={callerHistoryData?.entries || []}
            isLoading={isCallerHistoryLoading}
            isError={isCallerHistoryError}
            isPhoneReady={isPhoneReady}
          />
        </Stack>
      </div>

      {/* More Caller Information Section */}
      <div className=" rounded-md">
        <Stack gap="md">
          {/* Prefix and Name Row */}
          <Group grow>
            <Controller
              name="prefix"
              control={control}
              render={({ field }) => (
                <Select
                  label="Prefix"
                  placeholder="Select caller's prefix"
                  required
                  data={prefixOptions}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.prefix?.message}
                  disabled={disabled}
                />
              )}
            />

            <TextInput
              label="First Name"
              placeholder="Enter caller's first name"
              required
              register={register('first_name')}
              error={errors.first_name?.message}
              disabled={disabled}
            />

            <TextInput
              label="Last Name"
              placeholder="Enter caller's last name"
              required
              register={register('last_name')}
              error={errors.last_name?.message}
              disabled={disabled}
            />
          </Group>

          {/* Date of Birth and Location Row */}
          <Group grow>
            <Controller
              name="date_of_birth"
              control={control}
              render={({ field }) => (
                <DateInput
                  label="Date of Birth"
                  placeholder="Caller's date of birth"
                  required
                  value={field.value ? new Date(field.value) : null}
                  onChange={date => field.onChange(date)}
                  error={errors.date_of_birth?.message}
                  disabled={disabled}
                  maxDate={new Date()}
                />
              )}
            />

            {isLoaded ? (
              <Controller
                name="location"
                control={control}
                render={({ field }) => (
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Location <span className="text-red-500 ml-1">*</span>
                    </label>
                    <Autocomplete
                      onLoad={onLoad}
                      onPlaceChanged={onPlaceChanged}
                      options={{
                        componentRestrictions: { country: 'ng' }, // Restrict to Nigeria
                        fields: [
                          'formatted_address',
                          'geometry',
                          'address_components',
                        ],
                      }}
                    >
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 z-10 pointer-events-none" />
                        <input
                          type="text"
                          placeholder="Provide caller's address or current location"
                          value={field.value || ''}
                          onChange={e => {
                            field.onChange(e.target.value)
                            // Clear coordinates if user manually edits
                            form.setValue('location_latitude', undefined)
                            form.setValue('location_longitude', undefined)
                            form.setValue('location_state', undefined)
                          }}
                          disabled={disabled}
                          className="w-full pl-10 pr-4 py-2 rounded-md"
                          style={{
                            backgroundColor: '#F5F5F7',
                            border: 'none',
                            paddingLeft: '2.5rem',
                            fontSize: '0.875rem',
                            color: disabled ? '#9CA3AF' : '#111827',
                            cursor: disabled ? 'not-allowed' : 'text',
                          }}
                        />
                      </div>
                    </Autocomplete>
                    {errors.location && (
                      <p
                        className="text-red-500"
                        style={{
                          fontSize: '10px',
                          marginTop: '4px',
                        }}
                      >
                        {errors.location.message}
                      </p>
                    )}
                  </div>
                )}
              />
            ) : (
              <TextInput
                label="Location"
                placeholder="Provide caller's address or current location"
                required
                register={register('location')}
                error={errors.location?.message}
                leftSection={<MapPin className="w-4 h-4 text-gray-400" />}
                disabled={disabled}
              />
            )}
          </Group>
        </Stack>
      </div>

      {/* Call Classification Section */}
      <div className="mb-20">
        <Group mb="md">
          <Text size="md" fw={600} c="primary.6">
            Call Classification
          </Text>
        </Group>

        <Stack gap="md">
          {/* Call Type and Category Row */}
          <Group grow>
            <Controller
              name="call_type"
              control={control}
              render={({ field }) => (
                <Select
                  label="Call Type"
                  placeholder="Select call type"
                  required
                  data={callTypeOptions}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.call_type?.message}
                  disabled={disabled}
                />
              )}
            />

            <Controller
              name="call_category"
              control={control}
              render={({ field }) => (
                <Select
                  label="Call Category"
                  placeholder="Select subcategory"
                  required
                  data={currentSubcategories}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.call_category?.message}
                  disabled={disabled}
                />
              )}
            />
          </Group>
        </Stack>
      </div>
    </div>
  )

  // Render based on mode
  if (mode === 'standalone') {
    return (
      <FormProvider {...standaloneForm}>
        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          id="caller-information-form"
        >
          {renderFormContent()}
        </form>
      </FormProvider>
    )
  }

  // FormContext mode - just render the content
  return renderFormContent()
}

// Export the form values type for external use
export type { CallerInformationFormValues } from '@/validation/schemas/callerInformation'
